import { useEffect, useMemo, useState } from 'react'
import type { LabourCost, LabourItem, LabourRole } from '@entities/calculator'
import {
  MAX_INPUT_VALUE,
  formatRub,
  parseInput,
  toDecimalString,
  useSaveRecipeLabourMutation,
} from '@entities/calculator'

interface LabourBlockProps {
  cardId: number
  roles: readonly LabourRole[]
  /** Часы, пришедшие с бэка. */
  items: readonly LabourItem[]
  /** Уже посчитанная стоимость работы — по сохранённым часам. */
  cost: LabourCost
  /** Для каких ролей заведена ставка: без неё строка ничего не добавит. */
  ratedRoleIds: ReadonlySet<number>
  isLoading: boolean
  /**
   * Набранные часы — по ним считается сумма, пока их не сохранили. Без этого
   * человек вводит часы и видит ноль, будто расчёт сломан.
   */
  onDraftChange: (items: LabourItem[]) => void
  onGoToRates: () => void
}

/**
 * «Работа» — часы исполнителей по этому рецепту. Ставка берётся из «Ставок»,
 * здесь только время: она одна на все рецепты, а часы у каждого свои.
 *
 * Форма отправляется целиком: ручка заменяет весь набор, пустое поле означает
 * «роль не привлекается».
 */
/**
 * Заполненные строки в том виде, в каком их принимает бэк. Пустое поле — роль
 * не привлекается, её не отправляем; нечитаемое значение считаем нулём, чтобы
 * сумма не превращалась в NaN прямо во время набора.
 */
const toItems = (
  roles: readonly LabourRole[],
  draft: Record<number, string>
): LabourItem[] =>
  roles
    .filter(role => (draft[role.role_id] ?? '') !== '')
    .map(role => ({
      role_id: role.role_id,
      hours: toDecimalString(parseInput(draft[role.role_id] ?? '') ?? 0, 2),
    }))

const LabourBlock = ({
  cardId,
  roles,
  items,
  cost,
  ratedRoleIds,
  isLoading,
  onDraftChange,
  onGoToRates,
}: LabourBlockProps) => {
  const save = useSaveRecipeLabourMutation()

  const saved = useMemo(() => {
    const map = new Map<number, string>()
    items.forEach(item => map.set(item.role_id, item.hours))
    return map
  }, [items])

  const [draft, setDraft] = useState<Record<number, string>>({})

  useEffect(() => {
    setDraft(Object.fromEntries(saved))
  }, [saved])

  // Черновик отдаём наверх: сумму по строке, итог работы и общую
  // себестоимость считает шторка, и всё это должно меняться на глазах.
  useEffect(() => {
    onDraftChange(toItems(roles, draft))
  }, [roles, draft, onDraftChange])

  const isDirty = roles.some(
    role => (draft[role.role_id] ?? '') !== (saved.get(role.role_id) ?? '')
  )

  const invalid = roles.some(role => {
    const raw = draft[role.role_id]
    if (!raw) return false
    const value = parseInput(raw)
    return value === null || value < 0 || value > MAX_INPUT_VALUE
  })

  const handleSave = () => {
    save.mutate({ cardId, items: toItems(roles, draft) })
  }

  return (
    <div className="cost-labour">
      <h3>
        Работа
        <span>{formatRub(cost.totalKopecks)}</span>
      </h3>

      {isLoading && <p className="cost-note">Загружаем часы…</p>}

      <p className="cost-labour__lead">
        Сколько часов уходит у каждого. Ставку за час задаёте один раз во
        вкладке «Ставки».
      </p>

      {roles.map(role => {
        const line = cost.lines.find(one => one.roleId === role.role_id)
        const hasRate = ratedRoleIds.has(role.role_id)
        return (
          <label key={role.role_id} className="cost-labour__row">
            <span className="cost-labour__name">{role.name}</span>
            <span className="cost-labour__field">
              <input
                type="text"
                inputMode="decimal"
                value={draft[role.role_id] ?? ''}
                onChange={event =>
                  setDraft(current => ({
                    ...current,
                    [role.role_id]: event.target.value,
                  }))
                }
                placeholder="0"
                aria-label={`Часы: ${role.name}`}
              />
              <span className="cost-labour__unit">ч</span>
            </span>
            <span className="cost-labour__sum">
              {!hasRate ? (
                <button
                  type="button"
                  className="cost-line__add"
                  onClick={onGoToRates}
                >
                  нет ставки
                </button>
              ) : (
                formatRub(line?.costKopecks ?? 0)
              )}
            </span>
          </label>
        )
      })}

      {save.isError && (
        <p className="cost-note warning">
          {save.error?.response?.status === 404
            ? 'Рецепта нет в ваших — верните его, чтобы сохранить часы'
            : 'Не удалось сохранить часы. Попробуйте ещё раз'}
        </p>
      )}

      {isDirty && (
        <button
          type="button"
          className="cost-button wide"
          onClick={handleSave}
          disabled={invalid || save.isLoading}
        >
          {save.isLoading ? 'Сохраняем…' : 'Сохранить часы'}
        </button>
      )}
    </div>
  )
}

export default LabourBlock
